import { css } from '@emotion/react';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useEffect, useRef, FC, useId } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';

import { SasIconButton } from './SasIcon';
import { theme } from './theme';

const styles = {
  backdrop: css`
    position: fixed;
    inset: 0;
    padding: 0;
    margin: 0;
    background: var(--background-overlay);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99;
    width: 100%;
  `,
  content: (fitContent: boolean | undefined, backgroundColor: string | undefined, padding: number | undefined) => css`
    border-radius: var(--border-radius-large);
    position: relative;
    padding: ${padding ?? theme.spacing.values.xxl}rem;
    max-width: ${fitContent ? undefined : theme.breakpoints.laptop};
    max-height: 92%;
    margin: 0 ${theme.spacing.xl};
    background-color: ${backgroundColor ?? 'var(--color-white)'};
    z-index: 100;
    overflow-y: auto;
    overflow-x: hidden;
    box-shadow: rgba(0, 0, 0, 0.2) 0 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px,
      rgba(0, 0, 0, 0.12) 0px 9px 46px 8px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      min-width: 100%;
      min-height: 100%;
      margin: 0;
      padding: ${padding ?? theme.spacing.values.sm}rem;
      border-radius: 0;
      box-shadow: none;
    }
  `,
  closeButton: (hasCalendarBar: boolean | undefined) => css`
    position: absolute;
    right: ${hasCalendarBar ? theme.spacing.xl : theme.spacing.md};
    top: ${hasCalendarBar ? theme.spacing.xl : theme.spacing.md};

    @media (max-width: ${theme.breakpoints.tablet}) {
      right: ${theme.spacing.sm};
      top: ${theme.spacing.sm};
    }
  `,
};

type Props = {
  open: boolean;
  children: React.ReactNode;
  contentBg?: string;
  showCloseButton?: boolean;
  handleClose?: (e: React.MouseEvent<HTMLElement>) => void;
  padding?: number;
  fitContent?: boolean;
  hasCalendarBar?: boolean;
};

export const SasModal: FC<Props> = props => {
  const {
    fitContent,
    open,
    children,
    contentBg,
    padding,
    hasCalendarBar = false,
    showCloseButton = true,
    handleClose = () => {},
  } = props;

  const el = useRef(document.createElement('div'));
  const modalId = useId();

  const getOrCreateModalRoot = () => {
    let modalRoot = document.getElementById(modalId);

    if (!modalRoot) {
      modalRoot = document.createElement('div');
      modalRoot.setAttribute('id', modalId);
      document.body.prepend(modalRoot);
    }

    return modalRoot;
  };

  useEffect(() => {
    const modalRoot = getOrCreateModalRoot();
    modalRoot.appendChild(el.current);

    return () => {
      modalRoot.parentNode?.removeChild(modalRoot);
    };
  }, []);

  useEffect(() => {
    if (open) {
      disableBodyScroll(el.current);
    } else {
      enableBodyScroll(el.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <FocusLock>
      <div css={styles.backdrop} onClick={handleClose}>
        <div css={styles.content(fitContent, contentBg, padding)} onClick={e => e.stopPropagation()}>
          {showCloseButton && (
            <SasIconButton
              css={styles.closeButton(hasCalendarBar)}
              name="cl--close"
              color="var(--color-gray-5)"
              onClick={handleClose}
            />
          )}
          {children}
        </div>
      </div>
    </FocusLock>,
    el.current
  );
};
